import { inject, Injectable } from '@angular/core';
import { LocationEntry, LocationsData, MeasurementEntry, normalizeLocationsDataPriorities, Survey, SurveyPhoto, SurveySheetType } from '../models/survey.model';
import { SurveyDatabase } from '../db/survey.db';
import { isConnectionError, notifyPersistenceFailure, withConnectionRetry } from '../db/dexie-hardening';
import { countSurvey, SurveyLogAction, SurveyLogContext, SurveyLogService } from './survey-log.service';
import { SurveySnapshotService } from './survey-snapshot.service';
import { AuthDtoSignerService } from '@damofire/data-security';
import { getBlob, ref, Storage } from '@angular/fire/storage';
import pLimit, { LimitFunction } from 'p-limit';
import { blobToBase64, deleteFSDirectory, deleteFSFile, isFileExists, logger, readFSFile, writeFSFile } from '@damofire/data-commons';

const INACTIVITY_THRESHOLD_DAYS = 90;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CLONE_PHOTO_CONCURRENCY = 8;

export class MoveConflictError extends Error {
  override name = 'MoveConflictError';
}

function moveRowSignature(row: Partial<LocationEntry>): string {
  // Full-row conflict check: a concurrent annotations (or any field) edit
  // during the photo copy must re-plan, or the stale rowToMove would erase it.
  return JSON.stringify(row ?? null);
}

const MAX_MOVE_PLAN_ATTEMPTS = 2;

interface MovePlan {
  surveyId: string;
  sourceMeasurementIndex: number;
  rowIndex: number;
  destinationMeasurementIndex: number;
  rowToMove: Partial<LocationEntry>;
  movedPhotos: SurveyPhoto[];
  plannedSignature: string;
  estimatedDestRowIndex: number;
  sourceDir: string;
  allCopied: boolean;
  createdPaths: string[];
}

const VALIDATION_ERROR_PATTERNS = [
  /Survey with id .* not found/,
  /Measurement not found/,
  /Source measurement at index .* not found/,
  /Location row .* not found/,
  /Location row or photos not found/,
  /Photo not found/,
  /Survey or installer photos not found/,
  /Survey not found/,
];

function errorMessage(e: unknown): string {
  if (e instanceof Error) {
    return e.message;
  }
  return typeof e === 'string' ? e : '';
}

function isValidationError(e: unknown): boolean {
  if (e instanceof MoveConflictError) {
    return true;
  }
  return VALIDATION_ERROR_PATTERNS.some((pattern) => pattern.test(errorMessage(e)));
}

function isSurveyNotFound(e: unknown): boolean {
  return /Survey with id .* not found/.test(errorMessage(e));
}

@Injectable({
  providedIn: 'root',
})
export class IdbSurveyService {
  private readonly db = inject(SurveyDatabase);
  private readonly dtoSigner = inject(AuthDtoSignerService);
  private readonly storage = inject(Storage);
  private readonly surveyLog = inject(SurveyLogService);
  private readonly snapshots = inject(SurveySnapshotService);

  async createSurvey(survey: Omit<Survey, 'id' | 'updatedAt'>): Promise<string> {
    const surveyId = crypto.randomUUID();
    const newSurvey: Survey = {
      ...survey,
      id: surveyId,
      updatedAt: Date.now(),
    };
    return this.surveyLog.track('survey.create', { surveyId, source: 'idb', after: countSurvey(newSurvey) }, async () => {
      await this.persist('surveys.createSurvey', () => this.db.surveys.add(newSurvey));
      await this.verifyStored(surveyId, 'createSurvey.verify');
      return surveyId;
    });
  }

  async getSurvey(id: string): Promise<Survey | undefined> {
    return this.logReadFailure('survey.load', id, async () => {
      const { result } = await withConnectionRetry('surveys.getSurvey', () => this.db.table('surveys').get(id));
      return result;
    });
  }

  async getAllSurveys(): Promise<Survey[]> {
    return this.logReadFailure('grid.load', undefined, async () => {
      const { result } = await withConnectionRetry('surveys.getAllSurveys', () => this.db.table('surveys').toArray());
      return result;
    });
  }

  /** Reads stay raw (callers handle the original error); the failure is logged with its context first. */
  private async logReadFailure<T>(action: SurveyLogAction, surveyId: string | undefined, read: () => Promise<T>): Promise<T> {
    try {
      return await read();
    } catch (error: unknown) {
      this.surveyLog.failure(action, error, { surveyId, source: 'idb' });
      throw error;
    }
  }

  /** `preserveUpdatedAt` keeps the incoming timestamp, e.g. when applying the online copy as it is. */
  async updateSurvey(survey: Survey, options: { preserveUpdatedAt?: boolean } = {}): Promise<void> {
    const surveyToUpdate: Survey = {
      ...survey,
      updatedAt: options.preserveUpdatedAt ? survey.updatedAt : Date.now(),
    };
    await this.surveyLog.track('survey.save', { surveyId: survey.id, source: 'idb' }, () =>
      this.persist('surveys.updateSurvey', () => this.db.table('surveys').put(surveyToUpdate)),
    );
  }

  async deleteSurvey(id: string): Promise<void> {
    await this.surveyLog.track('survey.delete', { surveyId: id, source: 'idb' }, async (tracked) => {
      tracked.before = await this.readCountsQuietly(id);
      await this.persist('surveys.deleteSurvey', () => this.db.table('surveys').delete(id));
      await this.removeSnapshotsQuietly(id);
    });
  }

  /** Orphaned snapshots are harmless, so a failed cleanup is logged and never fails the delete. */
  private async removeSnapshotsQuietly(surveyId: string): Promise<void> {
    try {
      await this.snapshots.remove(surveyId);
    } catch (error: unknown) {
      this.surveyLog.warn('survey.delete', 'Could not remove the survey snapshots', { surveyId, source: 'idb', error });
    }
  }

  /** Write with a single connection retry; a terminal failure is reported to the persistence-failure hook, then rethrown. */
  private async persist<T>(operation: string, write: () => Promise<T>): Promise<T> {
    try {
      const { result } = await withConnectionRetry(operation, write);
      return result;
    } catch (e) {
      this.fail(operation, 'surveys', e);
      throw e;
    }
  }

  private async readCountsQuietly(surveyId: string): Promise<ReturnType<typeof countSurvey> | undefined> {
    try {
      return countSurvey(await this.db.table('surveys').get(surveyId));
    } catch {
      return undefined;
    }
  }

  /** Reads the record back after a write; a missing record means it was NOT persisted. */
  private async verifyStored(surveyId: string, operation: string, cleanup?: () => Promise<void>): Promise<void> {
    const found = await this.getSurvey(surveyId);
    if (found) {
      return;
    }
    const failure = {
      storeName: 'surveys',
      operation,
      error: new Error('Dexie verification failed for key'),
      attempts: 1,
      happenedAt: new Date().toISOString(),
    };
    notifyPersistenceFailure(this.db.persistenceFailureHandler, failure);
    await cleanup?.();
    throw failure.error;
  }

  async patchHubspotPublishedAt(surveyId: string, timestamp: number): Promise<void> {
    await this.persist('surveys.patchHubspotPublishedAt', () => this.db.table('surveys').update(surveyId, { hubspotPublishedAt: timestamp }));
  }

  async patchHubspotTicketId(surveyId: string, ticketId: string): Promise<void> {
    await this.persist('surveys.patchHubspotTicketId', () => this.db.table('surveys').update(surveyId, { hubspotTicketId: ticketId }));
  }

  async deleteInactiveSurveys(): Promise<number> {
    const cutoffTimestamp = Date.now() - INACTIVITY_THRESHOLD_DAYS * MS_PER_DAY;
    const { result: inactiveSurveys } = await withConnectionRetry('surveys.deleteInactiveSurveys', () =>
      this.db.table('surveys').where('updatedAt').below(cutoffTimestamp).toArray(),
    );

    let deletedCount = 0;

    for (const survey of inactiveSurveys) {
      if (!this.isSafeToDeleteInactive(survey)) {
        this.surveyLog.warn('survey.delete', 'Skipped inactive cleanup, survey was never published', { surveyId: survey.id, source: 'idb', before: countSurvey(survey) });
        continue;
      }

      try {
        await this.deleteLocalSurveyData(survey);
        deletedCount++;
      } catch (error: unknown) {
        logger.error('Failed to delete inactive local survey', { surveyId: survey.id, category: 'survey', error });
      }
    }

    return deletedCount;
  }

  /** A survey that was never published exists only on this device, so cleanup must not remove it. */
  private isSafeToDeleteInactive(survey: Survey): boolean {
    return survey.lastPublishedDate !== undefined;
  }

  private async deleteLocalSurveyData(survey: Survey): Promise<void> {
    await this.deleteLocalPhotoDirectory(survey.id);
    await this.deleteSurvey(survey.id);
    logger.info('Deleted inactive local survey', { surveyId: survey.id, lastUpdatedAt: new Date(survey.updatedAt).toISOString(), category: 'survey' });
  }

  private async deleteLocalPhotoDirectory(surveyId: string): Promise<void> {
    try {
      await deleteFSDirectory(surveyId);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.warn('Could not delete local photo directory', { surveyId, error: errorMessage, category: 'survey' });
    }
  }

  private fail(operation: string, storeName: string, error: unknown): void {
    const attempts = isConnectionError(error) ? 2 : 1;
    notifyPersistenceFailure(this.db.persistenceFailureHandler, { storeName, operation, error, attempts, happenedAt: new Date().toISOString() });
  }

  /**
   * Single Dexie rw transaction for read-modify-write. Retry whole tx once
   * on connection errors only. Raw table ops inside join ambient tx.
   * Validation and conflict errors rethrow without failure hook.
   */
  private async mutateSurvey<T>(surveyId: string, operation: string, mutate: (survey: Survey) => Promise<T> | T): Promise<T> {
    try {
      const { result } = await withConnectionRetry(operation, () =>
        this.db.transaction('rw', this.db.surveys, async () => {
          const survey = await this.db.surveys.get(surveyId);
          if (!survey) {
            throw new Error(`Survey with id ${surveyId} not found`);
          }
          const out = await mutate(survey);
          survey.updatedAt = Date.now();
          await this.db.surveys.put(survey);
          return out;
        }),
      );
      return result;
    } catch (e) {
      if (!isValidationError(e)) {
        this.fail(operation, 'surveys', e);
      }
      throw e;
    }
  }

  /**
   * Same tx without put when nothing to do. Missing survey or mutate
   * returning false skips persist. Single read, no double-get.
   * Returns true only when a change was persisted.
   */
  private async mutateSurveyIfExists(surveyId: string, operation: string, mutate: (survey: Survey) => Promise<boolean | void> | boolean | void): Promise<boolean> {
    try {
      const { result } = await withConnectionRetry(operation, () =>
        this.db.transaction('rw', this.db.surveys, async () => {
          const survey = await this.db.surveys.get(surveyId);
          if (!survey) {
            return false;
          }
          const changed = await mutate(survey);
          if (changed === false) {
            return false;
          }
          survey.updatedAt = Date.now();
          await this.db.surveys.put(survey);
          return true;
        }),
      );
      return result;
    } catch (e) {
      if (!isValidationError(e)) {
        this.fail(operation, 'surveys', e);
      }
      throw e;
    }
  }

  async updateMeasurement(surveyId: string, measurementIndex: number, measurement: Omit<MeasurementEntry, 'name' | 'index'> & { name?: string }): Promise<MeasurementEntry> {
    return this.surveyLog.track('sheet.save', { surveyId, measurementIndex, source: 'idb' }, () => this.applyMeasurementUpdate(surveyId, measurementIndex, measurement));
  }

  private applyMeasurementUpdate(surveyId: string, measurementIndex: number, measurement: Omit<MeasurementEntry, 'name' | 'index'> & { name?: string }): Promise<MeasurementEntry> {
    return this.mutateSurvey(surveyId, 'surveys.updateMeasurement', (survey) => {
      const existingMeasurement = survey.measurements[measurementIndex];
      const name = measurement.name ?? existingMeasurement?.name ?? `Measurement - ${measurementIndex}`;
      const existingLocationData = existingMeasurement?.locationData;
      const existingData = existingMeasurement?.data ?? {};

      const { locationData: incomingLocationData, ...measurementBase } = measurement;
      const resolvedLocationData = incomingLocationData ?? existingLocationData;
      const mergedData = { ...existingData, ...measurementBase.data };

      const updatedMeasurement = (
        resolvedLocationData !== undefined
          ? { ...measurementBase, index: measurementIndex, name, data: mergedData, locationData: resolvedLocationData }
          : { ...measurementBase, index: measurementIndex, name, data: mergedData }
      ) as MeasurementEntry;

      survey.measurements[measurementIndex] = updatedMeasurement;
      return updatedMeasurement;
    });
  }

  async getMeasurement(surveyId: string, measurementIndex: number): Promise<MeasurementEntry | undefined> {
    const survey = await this.getSurvey(surveyId);
    return survey?.measurements[measurementIndex];
  }

  async deleteMeasurement(surveyId: string, measurementIndex: number): Promise<void> {
    await this.surveyLog.track('sheet.delete', { surveyId, measurementIndex, source: 'idb' }, async (tracked) => {
      let beforeDelete: Survey | undefined;
      await this.mutateSurvey(surveyId, 'surveys.deleteMeasurement', (survey) => {
        tracked.before = countSurvey(survey);
        beforeDelete = structuredClone(survey);
        delete survey.measurements[measurementIndex];
        tracked.after = countSurvey(survey);
      });
      await this.snapshotAfterCommit(beforeDelete, 'delete-sheet');
    });
  }

  /**
   * Snapshots cannot be written inside the survey transaction (other table), so the pre-change copy is
   * captured inside it and stored right after it commits. Best effort, never blocks the user action.
   */
  private async snapshotAfterCommit(preChange: Survey | undefined, reason: 'delete-sheet' | 'count-drop'): Promise<void> {
    if (preChange) {
      await this.snapshots.take(preChange, reason);
    }
  }

  async updateLocations(surveyId: string, measurementIndex: number, locations: Partial<LocationsData>): Promise<void> {
    await this.surveyLog.track('location.save', { surveyId, measurementIndex, source: 'idb' }, async (tracked) => {
      let beforeShrink: Survey | undefined;
      await this.mutateSurvey(surveyId, 'surveys.updateLocations', (survey) => {
        // Reset on every run: a retried transaction re-runs this callback.
        beforeShrink = undefined;
        tracked.before = countSurvey(survey);
        if (this.locationsWouldShrink(survey, measurementIndex, locations)) {
          this.surveyLog.warn('location.save', 'Location count dropped on save', { surveyId, measurementIndex, source: 'idb', before: tracked.before });
          beforeShrink = structuredClone(survey);
        }

        if (!survey.measurements[measurementIndex]) {
          survey.measurements[measurementIndex] = this.createDefaultMeasurement(measurementIndex);
        }

        // Converge legacy stored values ('Critical Damage' / 'Damage') to the current vocabulary.
        normalizeLocationsDataPriorities(locations);
        survey.measurements[measurementIndex].locationData = locations;
        tracked.after = countSurvey(survey);
      });
      await this.snapshotAfterCommit(beforeShrink, 'count-drop');
    });
  }

  private locationsWouldShrink(survey: Survey, measurementIndex: number, incoming: Partial<LocationsData>): boolean {
    const storedCount = survey.measurements[measurementIndex]?.locationData?.locations?.length ?? 0;
    return (incoming.locations?.length ?? 0) < storedCount;
  }

  async getLocations(surveyId: string, measurementIndex: number): Promise<Partial<LocationsData> | undefined> {
    const survey = await this.getSurvey(surveyId);
    return survey?.measurements[measurementIndex]?.locationData;
  }

  createDefaultMeasurement(index: number, sheetType: SurveySheetType = 'measurement'): MeasurementEntry {
    return {
      index,
      name: `Measurement - ${index}`,
      sheetType,
      data: {},
    } as MeasurementEntry;
  }

  async addMeasurement(surveyId: string, sheetType: SurveySheetType): Promise<number> {
    return this.surveyLog.track('sheet.create', { surveyId, source: 'idb', sheetType }, async (tracked) => {
      const nextIndex = await this.mutateSurvey(surveyId, 'surveys.addMeasurement', (survey) => {
        tracked.before = countSurvey(survey);
        const indices = Object.keys(survey.measurements).map(Number).filter(Number.isFinite);
        const next = indices.length > 0 ? Math.max(...indices) + 1 : 1;

        survey.measurements[next] = this.createDefaultMeasurement(next, sheetType);
        tracked.after = countSurvey(survey);
        return next;
      });
      tracked.measurementIndex = nextIndex;
      return nextIndex;
    });
  }

  async duplicateMeasurement(surveyId: string, sourceMeasurementIndex: number): Promise<number> {
    return this.surveyLog.track('sheet.duplicate', { surveyId, measurementIndex: sourceMeasurementIndex, source: 'idb' }, (tracked) =>
      this.applyMeasurementDuplicate(surveyId, sourceMeasurementIndex, tracked),
    );
  }

  private applyMeasurementDuplicate(surveyId: string, sourceMeasurementIndex: number, tracked: SurveyLogContext): Promise<number> {
    return this.mutateSurvey(surveyId, 'surveys.duplicateMeasurement', (survey) => {
      const sourceMeasurement = survey.measurements[sourceMeasurementIndex];
      if (!sourceMeasurement) {
        throw new Error(`Source measurement at index ${sourceMeasurementIndex} not found`);
      }
      tracked.before = countSurvey(survey);

      const existingIndices = Object.keys(survey.measurements)
        .map(Number)
        .filter((value) => Number.isFinite(value));
      const nextIndex = Math.max(...existingIndices, 0) + 1;

      const clonedData = structuredClone(sourceMeasurement.data);

      // Intentional scope: duplicate carries structure + data only, not
      // location rows. Cloning rows would alias photo filepaths
      // (`<survey>/<measurement>/<row>/...`) to the source row's files.
      survey.measurements[nextIndex] = {
        index: nextIndex,
        name: `${sourceMeasurement.name} (Copy)`,
        sheetType: sourceMeasurement.sheetType,
        data: clonedData,
      } as MeasurementEntry;

      tracked.after = countSurvey(survey);
      return nextIndex;
    });
  }

  async addLocationPhoto(surveyId: string, measurementIndex: number, rowIndex: number, file: File): Promise<SurveyPhoto> {
    return this.surveyLog.track('photo.add', { surveyId, measurementIndex, source: 'idb', rowIndex }, () => this.storeLocationPhoto(surveyId, measurementIndex, rowIndex, file));
  }

  private async storeLocationPhoto(surveyId: string, measurementIndex: number, rowIndex: number, file: File): Promise<SurveyPhoto> {
    const base64 = await blobToBase64(file);
    const filepath = `${surveyId}/${measurementIndex}/${rowIndex}/${Date.now()}_${file.name}`;

    await writeFSFile(filepath, base64);

    const newPhoto: SurveyPhoto = {
      filepath,
      filename: file.name,
      createdAt: Date.now(),
      uploadStatus: 'offline',
    };

    try {
      await this.mutateSurvey(surveyId, 'surveys.addLocationPhoto', (survey) => {
        const measurement = survey.measurements[measurementIndex];
        if (!measurement) {
          throw new Error('Measurement not found');
        }
        if (!measurement.locationData) {
          measurement.locationData = { locations: [] } as LocationsData;
        }
        if (!measurement.locationData.locations) {
          measurement.locationData.locations = [];
        }
        while (measurement.locationData.locations.length <= rowIndex) {
          measurement.locationData.locations.push({});
        }
        const row = measurement.locationData.locations[rowIndex];
        row.photos = row.photos || [];
        row.photos.push(newPhoto);
      });
    } catch (e) {
      try {
        await deleteFSFile(filepath);
      } catch (cleanupError: unknown) {
        logger.warn('Photo compensation delete failed', { filepath, category: 'survey', error: cleanupError });
      }
      if (isSurveyNotFound(e)) {
        throw new Error('Survey not found');
      }
      throw e;
    }
    return newPhoto;
  }

  async updateLocationPhotoAnnotations(surveyId: string, measurementIndex: number, rowIndex: number, filepath: string, annotations: unknown[]): Promise<void> {
    try {
      await this.mutateSurvey(surveyId, 'surveys.updateLocationPhotoAnnotations', (survey) => {
        const measurement = survey.measurements[measurementIndex];
        if (!measurement) {
          throw new Error('Measurement not found');
        }
        const row = measurement.locationData?.locations?.[rowIndex];
        if (!row?.photos) {
          throw new Error('Location row or photos not found');
        }
        const photo = row.photos.find((p) => p.filepath === filepath);
        if (!photo) {
          throw new Error(`Photo not found: ${filepath}`);
        }
        photo.annotations = annotations;
        photo.annotationsDirty = true;
      });
    } catch (e) {
      if (isSurveyNotFound(e)) {
        throw new Error('Survey not found');
      }
      throw e;
    }
  }

  async removeLocationPhoto(surveyId: string, measurementIndex: number, rowIndex: number, photo: SurveyPhoto): Promise<void> {
    await this.surveyLog.track('photo.delete', { surveyId, measurementIndex, source: 'idb', rowIndex }, () => this.dropLocationPhoto(surveyId, measurementIndex, rowIndex, photo));
  }

  private async dropLocationPhoto(surveyId: string, measurementIndex: number, rowIndex: number, photo: SurveyPhoto): Promise<void> {
    const removed = await this.mutateSurveyIfExists(surveyId, 'surveys.removeLocationPhoto', (survey) => {
      const row = survey.measurements?.[measurementIndex]?.locationData?.locations?.[rowIndex];
      if (!row?.photos?.some((p) => p.filepath === photo.filepath)) {
        return false;
      }
      row.photos = row.photos.filter((p) => p.filepath !== photo.filepath);
      return true;
    });
    if (!removed) {
      return;
    }
    try {
      if (photo.filepath) {
        await deleteFSFile(photo.filepath);
      }
    } catch (e) {
      logger.warn('File deletion failed', { filepath: photo.filepath, category: 'survey', error: e });
    }
  }

  async getPhotoContent(path: string): Promise<string> {
    return readFSFile(path);
  }

  async addInstallerPhoto(surveyId: string, file: File): Promise<SurveyPhoto> {
    return this.surveyLog.track('photo.add', { surveyId, source: 'idb', installerPhoto: true }, () => this.storeInstallerPhoto(surveyId, file));
  }

  private async storeInstallerPhoto(surveyId: string, file: File): Promise<SurveyPhoto> {
    const base64 = await blobToBase64(file);
    const filepath = `${surveyId}/installer-notes/${Date.now()}_${file.name}`;

    await writeFSFile(filepath, base64);

    const newPhoto: SurveyPhoto = {
      filepath,
      filename: file.name,
      createdAt: Date.now(),
      uploadStatus: 'offline',
    };

    try {
      await this.mutateSurvey(surveyId, 'surveys.addInstallerPhoto', (survey) => {
        survey.installerPhotos = survey.installerPhotos || [];
        survey.installerPhotos.push(newPhoto);
      });
    } catch (e) {
      try {
        await deleteFSFile(filepath);
      } catch (cleanupError: unknown) {
        logger.warn('Photo compensation delete failed', { filepath, category: 'survey', error: cleanupError });
      }
      if (isSurveyNotFound(e)) {
        throw new Error('Survey not found');
      }
      throw e;
    }
    return newPhoto;
  }

  async removeInstallerPhoto(surveyId: string, photo: SurveyPhoto): Promise<void> {
    await this.surveyLog.track('photo.delete', { surveyId, source: 'idb', installerPhoto: true }, () => this.dropInstallerPhoto(surveyId, photo));
  }

  private async dropInstallerPhoto(surveyId: string, photo: SurveyPhoto): Promise<void> {
    const removed = await this.mutateSurveyIfExists(surveyId, 'surveys.removeInstallerPhoto', (survey) => {
      if (!survey?.installerPhotos?.some((p) => p.filepath === photo.filepath)) {
        return false;
      }
      survey.installerPhotos = survey.installerPhotos.filter((p) => p.filepath !== photo.filepath);
      return true;
    });
    if (!removed) {
      return;
    }
    try {
      if (photo.filepath) {
        await deleteFSFile(photo.filepath);
      }
    } catch (e) {
      logger.warn('File deletion failed', { filepath: photo.filepath, category: 'survey', error: e });
    }
  }

  async updateInstallerPhotoAnnotations(surveyId: string, filepath: string, annotations: unknown[]): Promise<void> {
    try {
      await this.mutateSurvey(surveyId, 'surveys.updateInstallerPhotoAnnotations', (survey) => {
        if (!survey?.installerPhotos) {
          throw new Error('Survey or installer photos not found');
        }
        const photo = survey.installerPhotos.find((p) => p.filepath === filepath);
        if (!photo) {
          throw new Error(`Photo not found: ${filepath}`);
        }
        photo.annotations = annotations;
        photo.annotationsDirty = true;
      });
    } catch (e) {
      if (isSurveyNotFound(e)) {
        throw new Error('Survey or installer photos not found');
      }
      throw e;
    }
  }

  async moveLocation(surveyId: string, sourceMeasurementIndex: number, rowIndex: number, destinationMeasurementIndex: number): Promise<void> {
    const context: SurveyLogContext = { surveyId, measurementIndex: sourceMeasurementIndex, source: 'idb', rowIndex, destinationMeasurementIndex };
    await this.surveyLog.track('location.move', context, (tracked) => this.planAndCommitMove(surveyId, sourceMeasurementIndex, rowIndex, destinationMeasurementIndex, tracked));
  }

  private async planAndCommitMove(
    surveyId: string,
    sourceMeasurementIndex: number,
    rowIndex: number,
    destinationMeasurementIndex: number,
    tracked: SurveyLogContext,
  ): Promise<void> {
    // FS avant, IDB en tx. Re-planifie une fois si concurrent change index ou contenu.
    let lastConflict: unknown;
    for (let attempt = 1; attempt <= MAX_MOVE_PLAN_ATTEMPTS; attempt++) {
      const plan = await this.planMove(surveyId, sourceMeasurementIndex, rowIndex, destinationMeasurementIndex);
      const conflict = await this.tryCommitMove(plan, attempt, tracked);
      if (conflict === null) {
        return;
      }
      lastConflict = conflict;
    }
    throw lastConflict;
  }

  private async planMove(surveyId: string, sourceMeasurementIndex: number, rowIndex: number, destinationMeasurementIndex: number): Promise<MovePlan> {
    const snapshot = await this.getSurvey(surveyId);
    if (!snapshot) {
      throw new Error(`Survey with id ${surveyId} not found`);
    }

    const sourceMeasurement = snapshot.measurements[sourceMeasurementIndex];
    const sourceLocations = sourceMeasurement?.locationData?.locations;

    if (!sourceLocations || rowIndex >= sourceLocations.length) {
      throw new Error(`Location row ${rowIndex} not found in measurement ${sourceMeasurementIndex}`);
    }

    const rowToMove = structuredClone(sourceLocations[rowIndex]);
    const plannedSignature = moveRowSignature(rowToMove);
    const plannedDest = snapshot.measurements[destinationMeasurementIndex];
    const estimatedDestRowIndex = plannedDest?.locationData?.locations?.length ?? 0;
    const sourceDir = `${surveyId}/${sourceMeasurementIndex}/${rowIndex}`;

    const { movedPhotos, allCopied, createdPaths } = await this.copyPhotosForMove(rowToMove.photos ?? [], surveyId, destinationMeasurementIndex, estimatedDestRowIndex);

    return {
      surveyId,
      sourceMeasurementIndex,
      rowIndex,
      destinationMeasurementIndex,
      rowToMove,
      movedPhotos,
      plannedSignature,
      estimatedDestRowIndex,
      sourceDir,
      allCopied,
      createdPaths,
    };
  }

  private async tryCommitMove(plan: MovePlan, attempt: number, tracked: SurveyLogContext): Promise<MoveConflictError | null> {
    try {
      await this.commitMovedRow(plan, tracked);

      // Only delete the source dir once every photo's bytes were copied to the destination.
      // If any copy failed we kept the original filepath + remote refs so the photo still renders;
      // deleting the source would then destroy the only local bytes it has.
      if (plan.allCopied) {
        await this.cleanupMoveDir(plan.sourceDir, 'source');
      }
      return null;
    } catch (e) {
      // Per-file cleanup only: the estimated dir may hold another move's
      // committed files after a concurrent move won the same destination.
      await this.cleanupMoveFiles(plan.createdPaths);
      if (e instanceof MoveConflictError && attempt < MAX_MOVE_PLAN_ATTEMPTS) {
        return e;
      }
      throw e;
    }
  }

  private async commitMovedRow(plan: MovePlan, tracked: SurveyLogContext): Promise<void> {
    const { surveyId, sourceMeasurementIndex, rowIndex, destinationMeasurementIndex, rowToMove, movedPhotos, plannedSignature, estimatedDestRowIndex } = plan;
    await this.mutateSurvey(surveyId, 'surveys.moveLocation', (fresh) => {
      const freshSource = fresh.measurements[sourceMeasurementIndex];
      const freshLocations = freshSource?.locationData?.locations;
      if (!freshLocations || rowIndex >= freshLocations.length) {
        throw new MoveConflictError(`Location row ${rowIndex} not found in measurement ${sourceMeasurementIndex}`);
      }
      if (moveRowSignature(freshLocations[rowIndex]) !== plannedSignature) {
        throw new MoveConflictError('Source row changed concurrently, re-plan move');
      }
      if (!fresh.measurements[destinationMeasurementIndex]) {
        fresh.measurements[destinationMeasurementIndex] = this.createDefaultMeasurement(destinationMeasurementIndex);
      }
      const dest = fresh.measurements[destinationMeasurementIndex];
      if (!dest.locationData) {
        dest.locationData = { locations: [] };
      }
      if (!dest.locationData.locations) {
        dest.locationData.locations = [];
      }
      if (dest.locationData.locations.length !== estimatedDestRowIndex) {
        throw new MoveConflictError('Destination index shifted concurrently, re-plan move');
      }
      tracked.before = countSurvey(fresh);
      if (freshSource.locationData) {
        freshSource.locationData.locations = freshLocations.filter((_, i) => i !== rowIndex);
      }
      dest.locationData.locations.push({ ...rowToMove, photos: movedPhotos });
      tracked.after = countSurvey(fresh);
    });
  }

  private async cleanupMoveDir(dir: string, kind: 'source' | 'destination'): Promise<void> {
    try {
      await deleteFSDirectory(dir);
    } catch (cleanupError: unknown) {
      logger.warn('Move cleanup failed', { dir, kind, category: 'survey', error: cleanupError });
    }
  }

  private async cleanupMoveFiles(paths: string[]): Promise<void> {
    for (const path of paths) {
      try {
        await deleteFSFile(path);
      } catch (cleanupError: unknown) {
        logger.warn('Move file cleanup failed', { filepath: path, category: 'survey', error: cleanupError });
      }
    }
  }

  private async copyPhotosForMove(
    photos: SurveyPhoto[],
    surveyId: string,
    destMeasurementIndex: number,
    destRowIndex: number,
  ): Promise<{ movedPhotos: SurveyPhoto[]; allCopied: boolean; createdPaths: string[] }> {
    const movedPhotos: SurveyPhoto[] = [];
    const createdPaths: string[] = [];
    let allCopied = true;

    for (const photo of photos) {
      const filenamePart = photo.filepath.split('/').pop() ?? photo.filename;
      // Unique per attempt: concurrent moves of the same row would otherwise
      // produce the same path, letting the loser's cleanup delete the winner's file.
      const attemptToken = crypto.randomUUID().split('-')[0];
      const newFilepath = `${surveyId}/${destMeasurementIndex}/${destRowIndex}/${Date.now()}_${attemptToken}_${filenamePart}`;

      try {
        const content = await readFSFile(photo.filepath);
        await writeFSFile(newFilepath, content);
        // Bytes now live at the destination path. Re-key to the new filepath and clear the
        // remote refs so publish re-uploads under the destination measurement/row route,
        // making the bucket file follow the destination index.
        movedPhotos.push({ ...photo, filepath: newFilepath, uploadStatus: 'offline', imageFolderPath: undefined, original: undefined, web: undefined, thumb: undefined });
        createdPaths.push(newFilepath);
      } catch (err) {
        // Local bytes unreadable (e.g. survey downloaded from Firestore, refs present but no FS bytes).
        // Keep the original filepath AND remote refs so the photo still renders and publish skips it.
        // The bucket file stays at the old index rather than becoming a broken reference.
        logger.warn('Could not copy photo during move, preserving original reference', { filepath: photo.filepath, category: 'survey', error: err });
        movedPhotos.push({ ...photo });
        allCopied = false;
      }
    }

    return { movedPhotos, allCopied, createdPaths };
  }

  async updateSurveyName(surveyId: string, surveyName: string): Promise<void> {
    await this.mutateSurvey(surveyId, 'surveys.updateSurveyName', (survey) => {
      if (!survey.contactInfo) {
        survey.contactInfo = {
          surveyName: '',
          dateOfSurvey: null,
          distributor: { hubspotId: null, name: '' },
          surveyor: { hubspotId: null, name: '' },
          salesRep: { hubspotId: null, name: '' },
          businessName: '',
          surveyType: '',
          contactPerson: '',
          surveyTime: '',
          phone: '',
          mobile: '',
          email: '',
          surveyAddress: '',
          city: '',
          stateProvince: '',
          zipPostalCode: '',
          sqFt: '',
          fiftyPlusSurvey: false,
          scissorLiftNeeded: false,
          laborNeeded: false,
          notes: '',
          installerNotes: '',
        };
      }

      survey.contactInfo.surveyName = surveyName;
    });
  }

  async cloneSurvey(surveyId: string, newSurveyName?: string): Promise<string> {
    return this.surveyLog.track('survey.duplicate', { surveyId, source: 'idb' }, async (tracked) => {
      const originalSurvey = await this.getSurvey(surveyId);
      if (!originalSurvey) {
        throw new Error(`Survey with id ${surveyId} not found`);
      }

      tracked.before = countSurvey(originalSurvey);
      const newSurveyId = await this.storeClone(originalSurvey, surveyId, newSurveyName);
      tracked.newSurveyId = newSurveyId;
      return newSurveyId;
    });
  }

  private async storeClone(originalSurvey: Survey, surveyId: string, newSurveyName?: string): Promise<string> {
    const newSurveyId = crypto.randomUUID();
    const clonedSurvey = this.prepareClonedSurvey(originalSurvey, newSurveyId, newSurveyName);

    await this.dtoSigner.sign(clonedSurvey, true);

    const photoLimit = pLimit(CLONE_PHOTO_CONCURRENCY);
    if (clonedSurvey.measurements) {
      await this.cloneMeasurementPhotos(clonedSurvey.measurements, surveyId, newSurveyId, photoLimit);
    }
    if (clonedSurvey.installerPhotos) {
      clonedSurvey.installerPhotos = await this.cloneInstallerPhotos(clonedSurvey.installerPhotos, surveyId, newSurveyId, photoLimit);
    }

    try {
      await this.persist('surveys.cloneSurvey', () => this.db.surveys.add(clonedSurvey));
    } catch (e) {
      await this.cleanUpClonePhotos(newSurveyId);
      throw e;
    }
    await this.verifyStored(newSurveyId, 'cloneSurvey.verify', () => this.cleanUpClonePhotos(newSurveyId));
    return newSurveyId;
  }

  private async cleanUpClonePhotos(newSurveyId: string): Promise<void> {
    try {
      await deleteFSDirectory(newSurveyId);
    } catch (cleanupError: unknown) {
      logger.warn('Clone cleanup failed', { surveyId: newSurveyId, category: 'survey', error: cleanupError });
    }
  }

  private prepareClonedSurvey(originalSurvey: Survey, newSurveyId: string, newSurveyName?: string): Survey {
    // Copy instead of mutating original. structuredClone only measurements
    // (bulk of tree) to avoid sharing refs that cloneMeasurementPhotos rewrites.
    const originalName = originalSurvey.contactInfo?.surveyName || 'Untitled Survey';
    const clonedSurvey: Survey = {
      ...originalSurvey,
      id: newSurveyId,
      updatedAt: Date.now(),
      contactInfo: originalSurvey.contactInfo ? { ...originalSurvey.contactInfo, surveyName: newSurveyName || `${originalName} - Copy` } : originalSurvey.contactInfo,
      measurements: originalSurvey.measurements ? structuredClone(originalSurvey.measurements) : originalSurvey.measurements,
      installerPhotos: originalSurvey.installerPhotos ? [...originalSurvey.installerPhotos] : originalSurvey.installerPhotos,
      quoteProducts: originalSurvey.quoteProducts ? [...originalSurvey.quoteProducts] : originalSurvey.quoteProducts,
    };

    delete clonedSurvey._id;
    delete clonedSurvey.lastPublishedDate;
    delete clonedSurvey.hubspotPublishedAt;
    delete clonedSurvey.hubspotTicketId;

    return clonedSurvey;
  }

  private async cloneMeasurementPhotos(measurements: Record<number, MeasurementEntry>, originalSurveyId: string, newSurveyId: string, photoLimit: LimitFunction): Promise<void> {
    await Promise.all(
      Object.entries(measurements).map(async ([, measurement]) => {
        if (measurement?.locationData?.locations) {
          await this.cloneLocationPhotos(measurement.locationData.locations, originalSurveyId, newSurveyId, photoLimit);
        }
      }),
    );
  }

  private async cloneLocationPhotos(locations: Partial<LocationEntry>[], originalSurveyId: string, newSurveyId: string, photoLimit: LimitFunction): Promise<void> {
    await Promise.all(
      locations.map(async (location) => {
        if (location.photos && location.photos.length > 0) {
          location.photos = await this.clonePhotosArray(location.photos, originalSurveyId, newSurveyId, photoLimit);
        }
      }),
    );
  }

  private async cloneInstallerPhotos(photos: SurveyPhoto[], originalSurveyId: string, newSurveyId: string, photoLimit: LimitFunction): Promise<SurveyPhoto[]> {
    const clones = await this.clonePhotosArray(photos, originalSurveyId, newSurveyId, photoLimit);
    return clones.map((clone) => this.withOriginalAnnotations(clone, photos, originalSurveyId, newSurveyId));
  }

  private withOriginalAnnotations(clone: SurveyPhoto, originals: SurveyPhoto[], originalSurveyId: string, newSurveyId: string): SurveyPhoto {
    const original = originals.find((photo) => photo.filepath.replace(originalSurveyId, newSurveyId) === clone.filepath);
    if (!original?.annotations) {
      return clone;
    }
    return { ...clone, annotations: structuredClone(original.annotations), annotationsDirty: original.annotationsDirty };
  }

  private async clonePhotosArray(photos: SurveyPhoto[], originalSurveyId: string, newSurveyId: string, photoLimit: LimitFunction): Promise<SurveyPhoto[]> {
    const results = await Promise.all(photos.map((photo) => photoLimit(() => this.cloneSinglePhoto(photo, originalSurveyId, newSurveyId))));
    return results.filter((photo): photo is SurveyPhoto => photo !== null);
  }

  private async cloneSinglePhoto(photo: SurveyPhoto, originalSurveyId: string, newSurveyId: string): Promise<SurveyPhoto | null> {
    const newFilepath = photo.filepath ? photo.filepath.replace(originalSurveyId, newSurveyId) : '';
    if (!newFilepath) {
      return null;
    }

    const photoContent = await this.readPhotoContentForClone(photo);
    if (photoContent === null) {
      return null;
    }

    try {
      await writeFSFile(newFilepath, photoContent);
    } catch (error) {
      logger.warn('Failed to clone photo', { filepath: photo.filepath, category: 'survey', error });
      return null;
    }

    return {
      filepath: newFilepath,
      filename: photo.filename,
      createdAt: Date.now(),
      uploadStatus: 'offline',
      ...(photo.size !== undefined ? { size: photo.size } : {}),
    };
  }

  private async readPhotoContentForClone(photo: SurveyPhoto): Promise<string | null> {
    const fileExists = await isFileExists(photo.filepath);
    if (fileExists) {
      return readFSFile(photo.filepath);
    }

    const storagePath = photo.original ?? photo.web;
    if (!storagePath) {
      logger.warn('Photo not found locally and has no remote path', { filepath: photo.filepath, category: 'survey' });
      return null;
    }

    try {
      return await this.downloadRemotePhotoContent(storagePath);
    } catch (remoteError) {
      logger.warn('Failed to clone photo from remote', { filepath: photo.filepath, category: 'survey', error: remoteError });
      return null;
    }
  }

  private async downloadRemotePhotoContent(storagePath: string): Promise<string> {
    return blobToBase64(await getBlob(ref(this.storage, storagePath)));
  }
}
